import { useState, useEffect } from 'react';
import useInterval from '../utils/useInterval'
import ready from './ready'

const DEFAULT_CHECK_BUILD_INTERVAL = 5 * 60 * 1000

// Returns true when there is a new build detected that needs to be activated.
// Set pollInterval to control how often to check for new builds.
export default function useUpdateAvailable(pollInterval) {
  if (!pollInterval) {
    pollInterval = DEFAULT_CHECK_BUILD_INTERVAL
  }

  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [checkUpdateError, setCheckUpdateError] = useState(false)
  useInterval(checkForNewBuilds, pollInterval)

  useEffect(() => {
    if (!import.meta.env.DEV) {
      console.log(`Checking for new builds every ${(pollInterval / 1000) > 60 ? `${(pollInterval / (60 * 1000))} minutes` : `${(pollInterval / 1000)} seconds`}`)
    }
    const unregisterDetectUpdate = detectUpdateAvailable()

    // Remove the event listener when the component unmounts
    return () => {
      unregisterDetectUpdate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function checkForNewBuilds() {
    ready()
      .then(registration => {
        if (registration) {
          registration.update()
            .catch(() => {
              if (!checkUpdateError) {
                console.log("Error checking for new build, is it available? (muting additional errors)")
                setCheckUpdateError(true)
              }
            })
        }
      })
  }

  function detectUpdateAvailable() {
    ready()
      .then(registration => {
        if (registration) {
          registration.addEventListener("updatefound", handleUpdateFound)

          const waitingWorker = registration.waiting;
          if (waitingWorker !== null) {
            setUpdateAvailable(true)
            return
          }
        }
      })

    // Return deregister callback
    return () => {
      ready()
        .then(registration => {
          if (registration) {
            registration.removeEventListener("updatefound", handleUpdateFound)
          }
        })
    }
  }

  function handleUpdateFound() {
    ready()
      .then(registration => {
        if (registration) {
          const installingWorker = registration.installing;
          if (installingWorker !== null) {
            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // At this point, the updated precached content has been fetched,
                  // but the previous service worker will still serve the older
                  // content until all client tabs are closed.
                  setUpdateAvailable(true)
                }
              }
            }
          }
        }
      })
  }

  return updateAvailable
}